import {
  Component,
  ElementRef,
  OnInit,
  QueryList,
  Renderer2,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { config } from '../../../../config/config';
import { VisitorCalendarActionCommand, VisitorSettingsCommand } from '../../../commands/pagination.query.command';
import { VisitorCreateEventCommand } from '../../../commands/visitor/event/create.event.command';
import { ButtonTriggerEvent } from '../../../common/components';
import { BaseComponent } from '../../../common/components/base.component';
import { DateTimeInputComponent } from '../../../common/components/datetime.input/datetime.input.component';
import { SuccessWrapComponent } from '../../../common/components/success.wrap/success.wrap.component';
import { localStorageKeys } from '../../../common/constants/local.storage.keys';
import { getRouteUrl, routeConfigs } from '../../../common/constants/route.config';
import { CanComponentDeactivate } from '../../../common/guards';
import { ModalService } from '../../../common/services/modal.service';
import { EventTimeService } from '../../../services/event.time.service';
import { HubApiService } from '../../../services/hub.api.service';
import { ImageService } from '../../../services/image.service';
import { VisitorApiService } from '../../../services/visitor.api.service';
import { AlertifyService } from '../../../services/alertify.service';
import { isNullOrUndefined } from '../../../common/extensions';
import { VisitorComponent } from '../visitor.component';
import { AllFrontendSettingsStore, VisitorCalendarStore } from '../../../stores/visitor.calendar.store';
import { AllFrontendSettingsCommand } from '../../../commands/visitor/all.frontend.settings.command';

@Component({
  selector: 'ig-visitor-create-event',
  templateUrl: './create.event.component.html',
  styleUrls: ['./create.event.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class VisitorCreateEventComponent extends BaseComponent implements OnInit, CanComponentDeactivate {
  public hubName: string;
  public resizeImageTemp = null;
  public timezoneLabel = '';
  public manualSetTimezone = false;
  public timezoneAddress: any;
  public isLoadingTimezone = false;
  public hasSetTimezone = false;
  public isEmailGDPRAllowed = false;
  public lastTimezone = null;
  public command = new VisitorCreateEventCommand();
  public showTimezoneCitySelector = false;
  public createEventForm: FormGroup;
  public frequencyItems = [];
  public frequencyEventTimes = [];
  public totalTimes = '';
  public dayInWeek = -1;
  public weekNo = -1;
  public submitSuccess = false;
  public isScrolledEnd = false;
  public isAllowOnlineEvents = true;
  public downwardArrow = config.downwardArrow;
  public visitorSettings = new VisitorSettingsCommand();
  public imageUploaderLabel2 = 'Please upload images in JPG, PNG or WEBP format. Landscape images recommended';
  public backUrl = routeConfigs.visitor.eventCalendar;
  public isImageChanged = false;
  public hasCopyrights = false;
  public frontendSettings: AllFrontendSettingsCommand;
  @ViewChild('container') public container: ElementRef;
  @ViewChild('description') public descriptionArea: ElementRef;
  @ViewChild('createEventSuccess') public successWrap: SuccessWrapComponent;
  @ViewChildren('dateTimeInput') public dateTimeInput: QueryList<DateTimeInputComponent>;

  public get timeRangeControls(): AbstractControl[] {
    return (<FormArray>this.createEventForm.get('timeRanges')).controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private imageService: ImageService,
    private alertify: AlertifyService,
    private visitorApiService: VisitorApiService,
    private visitorCalendarStore: VisitorCalendarStore,
    private allFrontendSettingsStore: AllFrontendSettingsStore,
    private render: Renderer2,
    private hubApiService: HubApiService
  ) {
    super({activatedRoute});
  }

  public ngOnInit() {
    let {hubName} = this.allRouteParams;
    this.hubName = hubName;
    this.command.defaultImage = config.defaultImageUrl;
    this.createForm();
    this.command.frequency = 'custom';
    this.resetFrequencyDropdown();
    this.addTimeRange();
    this.command.timezone = EventTimeService.getTimezoneCode();
    this.setDefaultTimeValue();
    this.resetTimezoneLabel();
    // this.setImageUploaderLabel2();
    this.downwardArrow = this.imageService.imageSourceUrl(this.downwardArrow);
    this.render.listen(this.container.nativeElement, 'scroll', () => {
      if (!this.container) {
        return false;
      }

      let {scrollTop, scrollHeight, clientHeight} = this.container.nativeElement;
      this.isScrolledEnd = scrollHeight - scrollTop === clientHeight;
    });

    this.allFrontendSettingsStore.allFrontendSettingsCommand
      .takeUntil(this.isDestroyed)
      .subscribe((command: AllFrontendSettingsCommand) => {
        this.frontendSettings = command;
        this.isAllowOnlineEvents = this.frontendSettings.customSettings.isAllowOnlineEvents;
      });

    // when going back, need to check where to back to, event calendar or trip planner
    // if from trip planner, the current url should be :hubName/submit-event
    this.activatedRoute.url.subscribe((data) => {
      const submitPath = routeConfigs.submit.event.path.replace(':hubName/', '');
      if (data.any((x) => x.path.includes(submitPath))) {
        this.backUrl = routeConfigs.tripPlanner.trip;
      }
    });
  }

  public descriptionChange() {
    this.descriptionArea.nativeElement.style.height =
      Math.min(this.descriptionArea.nativeElement.scrollHeight, 500) + 'px';
  }

  public virtualEventChange() {
    if (this.command.isOnlineEvent) {
      this.command.isHybridEvent = false;
    }
  }

  public onScroll($event) {
  }

  public hybridEventChange() {
    if (this.command.isHybridEvent) {
      this.command.isOnlineEvent = false;
    }
  }

  public createForm() {
    this.createEventForm = this.formBuilder.group({
      name: [null, [Validators.required]],
      description: [null, []],
      category: [null, []],
      timeRanges: this.formBuilder.array([]),
      address: [null, []],
      host: [null, [Validators.required]],
      venue: [null, []],
      timezone: [null, []],
      hostLink: [null, []],
      eventLink: [null, []],
      organizerEmail: [null, []],
      ticketUri: [null, []],
    });
  }

  public setFrequencyDropdown() {
    if (this.command.eventTimes.length > 0) {
      this.setDefaultTimeValue();
    }

    this.frequencyItems = this.frequencyItems.filter((x) => x.value !== 'last');

    let start = this.command.eventTimes[0].startTime;
    this.dayInWeek = EventTimeService.getDayOfWeek(start, this.command.timezone);
    let dayText = config.dayOfWeek[this.dayInWeek].name;

    let weekly = this.frequencyItems.first((x) => x.value === 'weekly');
    if (weekly) {
      weekly.text = `Weekly on ${dayText}`;
    }

    let biWeekly = this.frequencyItems.first((x) => x.value === 'biweekly');
    if (biWeekly) {
      biWeekly.text = `Every Other ${dayText} (starts from ${EventTimeService.getDayAndMonth(
        start,
        this.command.timezone
      )})`;
    }

    let monthly = this.frequencyItems.first((x) => x.value === 'monthly');
    if (monthly) {
      let no = EventTimeService.getWeekDayNo(start, this.command.timezone);
      if (!no.isLast) {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';
      } else if (no.isLast && no.text === 'last') {
        monthly.text = `Monthly on the last ${dayText}`;
        monthly.value = 'last';
      } else if (no.isLast && no.text !== 'last') {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';

        this.frequencyItems.push({
          value: 'last',
          text: `Monthly on the last ${dayText}`,
        });
      }
    }
  }

  public frequencySourceChange(target) {
    this.command.frequency = target.value;
    let start = this.command.eventTimes[0].startTime;
    let end = this.command.eventTimes[0].endTime;
    // end = EventTimeService.addHours(new Date(end), -1);
    let eventTimes = [],
      days = [];
    let diff = 0;

    switch (target.value) {
      case 'daily':
        diff = EventTimeService.diff(start, end);
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonth(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonth(end, this.command.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
        }
        break;
      case 'weekly':
        diff = EventTimeService.diff(start, end, 'weeks');
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * 7 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
        }
        break;
      case 'biweekly':
        diff = EventTimeService.diff(start, end, 'biweekly');
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * 14 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
        }
        break;
      case 'monthly':
        days = EventTimeService.getSameDayInWeek(start, end, this.dayInWeek, this.weekNo, this.command.timezone);
        this.totalTimes = `${days.length} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;
        days.forEach((x) => {
          let time = this.adjustForDaylightSaving(start, end, x);
          this.setEventTimes(x, end, eventTimes);
        });
        break;
      case 'last':
        days = EventTimeService.getLastWeekDays(start, end, this.dayInWeek, this.command.timezone);
        this.totalTimes = `${days.length} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;
        days.forEach((x) => {
          let time = this.adjustForDaylightSaving(start, end, x);
          this.setEventTimes(x, end, eventTimes);
        });
        break;
      default:
        break;
    }
    this.frequencyEventTimes = eventTimes;
  }

  public async createEvent(event: ButtonTriggerEvent) {
    // if (!this.hasSetTimezone) {
    if (!this.command.timezone) {
      this.alertify.warning('Please set the timezone');
      event.complete();
      return;
    }
    this.submitSuccess = false;
    let {hubName} = this.allRouteParams;
    this.command.isHubCreated = false;
    let timesText = [];
    this.command.eventTimes.forEach((time) => {
      timesText.push({
        startTimeText: EventTimeService.toNonTimezoneTimeText(time.startTime),
        endTimeText: EventTimeService.toNonTimezoneTimeText(time.endTime),
      });
    });
    this.command.eventTimesText = JSON.stringify(timesText);
    // if (this.command.image) {
    //     let imageFile = await this.imageService.compressImageFile(this.command.image);
    //     if (imageFile) {
    //         this.command.image = imageFile;
    //     }
    // }

    // image uploaded
    if (this.resizeImageTemp) {
      const res = await this.hubApiService.uploadBase64ImageToAWS(this.resizeImageTemp);
      if (res.success) {
        this.command.image = null;
        this.command.imageLink = res.url;
      }
    }
    this.visitorApiService
      .createEvent(hubName, this.command, this.frequencyEventTimes)
      .finally(event.complete)
      .subscribe(() => {
        this.submitSuccess = true;
        this.command = new VisitorCreateEventCommand();
        this.createForm();
        this.command.frequency = 'custom';
        this.resetFrequencyDropdown();
        this.addTimeRange();
        this.setDefaultTimeValue();
        this.lastTimezone ? (this.command.timezone = this.lastTimezone) : this.resetTimezoneLabel();
        this.descriptionArea.nativeElement.style.height = '40px';
        this.resizeImageTemp = null;
        this.isImageChanged = false;
        this.hasCopyrights = false;
      });
  }

  public async onImageChange(file) {
    if (file) {
      this.resizeImageTemp = await this.imageService.compressImageFileToBase64(file, config.compressImgWidth);
      this.isImageChanged = true;
    }
  }

  public imageResized(imgBase64: string) {
    this.resizeImageTemp = imgBase64;
  }

  public get isFormInvalid() {
    let noTime = this.command.eventTimes.any((time) => !time.startTime || !time.endTime);
    // return this.createEventForm.invalid || !this.command.eventTimes || !this.command.eventTimes.length || noTime;
    return (
      this.createEventForm.invalid ||
      !this.command.eventTimes ||
      !this.command.eventTimes.length ||
      noTime ||
      (!this.command.isOnlineEvent && !this.command.address)
    );
  }

  public goBack() {
    this.visitorCalendarStore.actionCommand.next(new VisitorCalendarActionCommand());
    this.router.navigateByUrl(
      getRouteUrl(this.backUrl, {
        hubName: this.hubName,
      }),
      {skipLocationChange: this.noChangeUrlWhenRedirect}
    );
  }

  public setEventTime(time, timeRange) {
    if (time.startTime) {
      timeRange.startTime = time.startTime;
    }
    if (time.endTime) {
      timeRange.endTime = time.endTime;
    }
    this.frequencySourceChange({value: this.command.frequency});
  }

  public addTimeRange() {
    this.command.eventTimes.push({});

    // let timeRangeGroups = (<FormArray>this.createEventForm.get('timeRanges')).controls;
    // let getTimeRangeFunc = () => {
    //     let group = timeRangeGroups[this.command.eventTimes.length - 1];
    //     return group ? {start: group.get('startTime').value, end: group.get('endTime').value} : null;
    // };
    // let getOtherTimeRangesFunc = () => {
    //     let otherGroups = timeRangeGroups.slice(0, this.command.eventTimes.length - 1);
    //     return otherGroups.map(group => {
    //         return {start: group.get('startTime').value, end: group.get('endTime').value};
    //     });
    // };
    // let timeRangeValidator = timeRangeOverlapValidate(getTimeRangeFunc, getOtherTimeRangesFunc);
    // (<FormArray>this.createEventForm.get('timeRanges'))
    //     .push(this.formBuilder.group({
    //         startTime: [null, [Validators.required, timeRangeValidator]],
    //         endTime: [null, [Validators.required, timeRangeValidator]]
    //     }));
  }

  public removeTimeRange(i) {
    this.command.eventTimes.splice(i, 1);
    //(<FormArray>this.createEventForm.get('timeRanges')).removeAt(i);
  }

  public canDeactivate() {
    if (this.createEventForm.pristine) {
      return Observable.of(true);
    }

    let allowNavigate = new Subject<boolean>();

    this.modalService.showUnSaveWarning({allowNavigate});

    return allowNavigate;
  }

  public resetTimezoneLabel() {
    // this.timezoneLabel = 'Timezone *(enter a city to set timezone)';

    let timezoneFormatterDefault = localStorage.getItem(localStorageKeys.timezoneFormatterDefault);
    if (timezoneFormatterDefault) {
      this.command.timezone = EventTimeService.getTimezoneCodeByFormatter(timezoneFormatterDefault);
    } else {
      this.command.timezone = EventTimeService.getTimezoneCode();
    }
    this.timezoneLabel = `Timezone (${this.command.timezone})`;
  }

  public citySelected() {
    if (this.timezoneAddress) {
      this.isLoadingTimezone = true;
      this.hubApiService
        .getTimezoneByLatlng(this.timezoneAddress.lat, this.timezoneAddress.lng)
        .finally(() => {
          this.isLoadingTimezone = false;
        })
        .subscribe((data) => {
          console.log(data);
          this.command.timezone = data.abbreviation;
          this.hasSetTimezone = true;
          this.lastTimezone = this.command.timezone;
          this.timezoneLabel = `Timezone (${this.command.timezone})`;
          this.dateTimeInput.toArray().forEach((x) => {
            x.timeFormatter = EventTimeService.getTimezoneFormatterByTimezone(this.command.timezone);
            x.timezone = this.command.timezone;
            x.setTime('start');
            x.setTime('end');
          });
        });
    }
  }

  private adjustForDaylightSaving(start, end, nextDayStart) {
    const formatter = EventTimeService.getTimezoneFormatterByTimezone(this.command.timezone);
    const startTimezone = EventTimeService.getTimezonecodeWithTimeSaving(start, formatter);
    const nextDayTimezone = EventTimeService.getTimezonecodeWithTimeSaving(nextDayStart, formatter);
    let thisEnd = end;
    if (startTimezone !== nextDayTimezone) {
      if (nextDayTimezone.indexOf('ST') >= 0) {
        nextDayStart = EventTimeService.addHours(nextDayStart, 1);
        thisEnd = EventTimeService.addHours(thisEnd, 1);
      } else {
        nextDayStart = EventTimeService.addHours(nextDayStart, -1);
        thisEnd = EventTimeService.addHours(thisEnd, -1);
      }
    }
    return {
      startTime: nextDayStart,
      endTime: thisEnd,
    };
  }

  private resetFrequencyDropdown() {
    this.frequencyItems = [
      {
        text: 'Custom',
        value: 'custom',
        selected: true,
      },
      {
        text: 'Daily',
        value: 'daily',
      },
      {
        text: 'Weekly',
        value: 'weekly',
      },
      {
        text: 'Biweekly',
        value: 'biweekly',
      },
      {
        text: 'Monthly',
        value: 'monthly',
      },
    ];
  }

  private setEventTimes(start, end, eventTimes) {
    let startTime = start;
    let endTime = new Date(new Date(startTime).setUTCHours(new Date(end).getUTCHours()));
    endTime = new Date(endTime.setUTCMinutes(new Date(end).getUTCMinutes()));
    if (endTime < new Date(startTime)) {
      endTime = new Date(EventTimeService.addHours(endTime, 24));
    }
    eventTimes.push({
      startTime: startTime,
      endTime: endTime.toISOString(),
    });
  }

  private setDefaultTimeValue() {
    let endPlusHours = 0;
    switch (this.command.frequency) {
      case 'custom':
        endPlusHours = 24 + 3;
        break;
      case 'daily':
        endPlusHours = 24 * 7 + 3;
        break;
      case 'weekly':
        endPlusHours = 24 * 365 + 3;
        break;
    }
    if (!this.command.eventTimes[0].startTime && !this.command.eventTimes[0].endTime) {
      this.command.eventTimes = [
        {
          startTime: EventTimeService.getDefaultStartTime(this.command.timezone),
          endTime: EventTimeService.addHours(EventTimeService.getDefaultStartTime(this.command.timezone), endPlusHours),
        },
      ];
    } else if (this.command.eventTimes[0].startTime && !this.command.eventTimes[0].endTime) {
      this.command.eventTimes = [
        {
          startTime: this.command.eventTimes[0].startTime,
          endTime: EventTimeService.addHours(new Date(this.command.eventTimes[0].startTime), 24 * 7),
        },
      ];
    } else if (!this.command.eventTimes[0].startTime && this.command.eventTimes[0].endTime) {
      this.command.eventTimes = [
        {
          startTime: EventTimeService.addHours(new Date(this.command.eventTimes[0].startTime), 24 * 7 * -1),
          endTime: this.command.eventTimes[0].endTime,
        },
      ];
    }
  }

  public get addressInputLabel() {
    if (!this.isAllowOnlineEvents) {
      return `Location / Address *`;
    }
    return `Location / Address ${this.command.isOnlineEvent ? '' : '*'} (or check Virtual Events)`;
  }

  private setImageUploaderLabel2() {
    const settingStorage = localStorage.getItem(localStorageKeys.visitorSettings);
    if (settingStorage) {
      this.visitorSettings = JSON.parse(settingStorage) as VisitorSettingsCommand;
      if (this.visitorSettings.calendarStyle.event === 'square') {
        this.imageUploaderLabel2 = this.imageUploaderLabel2.replace('360px', '640px');
      }
      if (!isNullOrUndefined(this.visitorSettings.isAllowOnlineEvents)) {
        this.isAllowOnlineEvents = this.visitorSettings.isAllowOnlineEvents;
      }
    }
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/visitor/create.event/create.event.component.ts